import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Mail, Plus, Search, UserRound } from 'lucide-react';
import { resendUserInvite, revokeUserInvite } from '@/api/generated/sdk.gen';
import { usersQuery, rolesQuery, invitesQuery, type User, type UserInvite } from '@/lib/users';
import { createInvite } from '@/lib/invites';
import { usePermissions } from '@/lib/me';
import { ApiError, messageFromError } from '@/lib/api';
import { formatRole } from '@/lib/nav';
import { formatDateTime, relativeDateTime } from '@/lib/format';
import { posthog } from '@/lib/posthog';
import { usePageHeader } from '@/components/PageHeader';
import { useConfirm } from '@/components/DialogProvider';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Avatar } from '@/components/ui/avatar';

export const Route = createFileRoute('/_app/users/')({
  loader: ({ context }) => context.queryClient.ensureQueryData(usersQuery()),
  component: UsersPage,
});

export function UsersPage() {
  const [inputValue, setInputValue] = useState('');
  const [q, setQ] = useState('');
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const { data, isLoading, error } = useQuery(usersQuery({ q, includeDisabled }));
  const users = data ?? [];

  const is401 = error instanceof ApiError && error.status === 401;
  const errorMessage = is401 ? 'Your session has expired.' : error ? messageFromError(error) : null;

  // Title lives in the Topbar (navTitle); publish the live count as the
  // subtitle and the Invite action into the Topbar actions slot.
  usePageHeader({
    subtitle: data ? `${users.length} user${users.length === 1 ? '' : 's'}` : undefined,
    actions: (
      <Button variant="primary" size="sm" onClick={() => setShowInvite(true)}>
        <Plus className="h-4 w-4" />
        Invite
      </Button>
    ),
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(inputValue.trim());
  }

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearch} className="flex min-w-[240px] flex-1 gap-2">
          <Input
            type="search"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Name or email…"
            leadingIcon={<Search />}
            aria-label="Search users by name or email"
            className="flex-1"
          />
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>
        <Checkbox
          label="Include disabled"
          checked={includeDisabled}
          onChange={(e) => setIncludeDisabled(e.target.checked)}
        />
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {errorMessage && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
          <span>
            {errorMessage}
            {is401 && (
              <>
                {' '}
                <Link to="/login" className="font-semibold underline">
                  Sign in again
                </Link>
                .
              </>
            )}
          </span>
        </div>
      )}

      {!isLoading && !errorMessage && data && users.length === 0 && (
        <div className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-hairline)] bg-card p-10 text-center">
          <UserRound className="mx-auto mb-3 h-8 w-8 text-muted" aria-hidden="true" />
          <p className="text-sm text-muted">
            {q ? 'No users found. Try a different search term.' : 'No team members yet.'}
          </p>
        </div>
      )}

      {users.length > 0 && (
        <section>
          <UsersTable users={users} />
        </section>
      )}

      <PendingInvitesSection />

      {showInvite && <InviteDialog onClose={() => setShowInvite(false)} />}
    </>
  );
}

export function UsersTable({ users }: { users: User[] }) {
  return (
    <div className="overflow-x-auto overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            <th scope="col" className="px-4 py-3">Name</th>
            <th scope="col" className="px-4 py-3">Email</th>
            <th scope="col" className="px-4 py-3">Status</th>
            <th scope="col" className="px-4 py-3">Roles</th>
            <th scope="col" className="px-4 py-3">Last login</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.id}
              className="border-b border-[var(--border-soft)] transition last:border-b-0 hover:bg-[var(--surface-sunken)]"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <Avatar name={u.display_name || u.email} size="sm" />
                  <Link
                    to="/users/$id"
                    params={{ id: u.id }}
                    className="font-semibold text-primary hover:underline"
                  >
                    {u.display_name}
                  </Link>
                </div>
              </td>
              <td className="px-4 py-3 text-muted">{u.email}</td>
              <td className="px-4 py-3">
                <UserStatusBadge status={u.status} />
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {u.roles.length === 0 ? (
                    <span className="text-muted">—</span>
                  ) : (
                    u.roles.map((r) => <RoleBadge key={r} role={r} />)
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-muted">
                {u.last_login_at ? relativeDateTime(u.last_login_at) : 'Never'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function UserStatusBadge({ status }: { status: User['status'] }) {
  return status === 'active' ? (
    <Badge variant="success" dot>
      Active
    </Badge>
  ) : (
    <Badge variant="danger" dot>
      Disabled
    </Badge>
  );
}

export function RoleBadge({ role }: { role: string }) {
  return <Badge variant="navy">{formatRole(role)}</Badge>;
}

export function InviteDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: roles, isLoading: rolesLoading } = useQuery(rolesQuery);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // Default the role picker to the first role in the catalogue once it loads
  // (real role codes only — never a hardcoded guess).
  useEffect(() => {
    if (!role && roles && roles.length > 0) setRole(roles[0]?.code ?? '');
  }, [roles, role]);

  const roleOptions = (roles ?? []).map((r) => ({ value: r.code, label: formatRole(r.code) }));

  const invite = useMutation({
    mutationFn: async () => {
      const trimmed = email.trim();
      if (!trimmed) throw new Error('Email is required.');
      if (!role) throw new Error('Choose a role.');
      return createInvite({ body: { email: trimmed, role }, idempotencyKey: crypto.randomUUID() });
    },
    onSuccess: (data) => {
      // Refresh the Pending invites section so the newly-created invite shows
      // up immediately (it renders below in the same page).
      qc.invalidateQueries({ queryKey: ['user-invites'] });
      toast({
        kind: 'success',
        title: 'Invite sent',
        message: `An invitation is on its way to ${data.email}.`,
      });
      posthog.capture?.('pms_action', { action: 'user_invite_created' });
      onClose();
    },
    onError: (err) => {
      setError(messageFromError(err));
      emailRef.current?.focus();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Invite teammate"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={invite.isPending}
            disabled={invite.isPending}
            onClick={() => {
              setError(null);
              invite.mutate();
            }}
          >
            Send invite
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Email"
          ref={emailRef}
          type="email"
          required
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={invite.isError || undefined}
          leadingIcon={<Mail />}
          placeholder="teammate@example.com"
          autoFocus
        />
        <Select
          label="Role"
          options={roleOptions}
          placeholder={rolesLoading ? 'Loading roles…' : roleOptions.length === 0 ? 'No roles available' : undefined}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={rolesLoading || roleOptions.length === 0}
        />
        {error && (
          <p role="alert" aria-live="assertive" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// PendingInvitesSection — invites created but not yet accepted. Only rendered
// for operators who can manage invites; resend/revoke both hit the server so
// the 401/403 backend enforcement is the real gate, this is UX only.
function PendingInvitesSection() {
  const { can } = usePermissions();
  const { data, isLoading, error } = useQuery({ ...invitesQuery, enabled: can('users.invite') });

  if (!can('users.invite')) return null;

  const invites = data ?? [];

  return (
    <section className="mt-8">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Pending invites</h2>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {messageFromError(error)}
        </p>
      )}

      {!isLoading && !error && invites.length === 0 && (
        <p className="text-sm text-muted">No pending invites.</p>
      )}

      {invites.length > 0 && (
        <div className="overflow-x-auto overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                <th scope="col" className="px-4 py-3">Email</th>
                <th scope="col" className="px-4 py-3">Role</th>
                <th scope="col" className="px-4 py-3">Expires</th>
                <th scope="col" className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <InviteRow key={inv.id} invite={inv} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function InviteRow({ invite }: { invite: UserInvite }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const askConfirm = useConfirm();

  const resendMut = useMutation({
    mutationFn: async () => {
      const res = await resendUserInvite({ throwOnError: true, path: { inviteId: invite.id } });
      return res.data;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['user-invites'] });
      toast({ kind: 'success', title: 'Invite resent', message: updated.email });
      posthog.capture?.('pms_action', { action: 'user_invite_resent' });
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Could not resend invite', message: messageFromError(err) });
    },
  });

  const revokeMut = useMutation({
    mutationFn: async () => revokeUserInvite({ throwOnError: true, path: { inviteId: invite.id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-invites'] });
      toast({ kind: 'success', title: 'Invite revoked', message: invite.email });
      posthog.capture?.('pms_action', { action: 'user_invite_revoked' });
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Could not revoke invite', message: messageFromError(err) });
    },
  });

  async function handleRevoke() {
    const ok = await askConfirm({
      title: 'Revoke invite?',
      body: `${invite.email} will no longer be able to accept this invite.`,
      destructive: true,
      confirmLabel: 'Revoke invite',
    });
    if (ok) revokeMut.mutate();
  }

  return (
    <tr className="border-b border-[var(--border-soft)] transition last:border-b-0 hover:bg-[var(--surface-sunken)]">
      <td className="px-4 py-3">{invite.email}</td>
      <td className="px-4 py-3">
        <RoleBadge role={invite.role} />
      </td>
      <td className="px-4 py-3 text-muted">{formatDateTime(invite.expires_at)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            loading={resendMut.isPending}
            disabled={resendMut.isPending}
            onClick={() => resendMut.mutate()}
          >
            Resend
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={revokeMut.isPending}
            disabled={revokeMut.isPending}
            onClick={handleRevoke}
          >
            Revoke
          </Button>
        </div>
      </td>
    </tr>
  );
}
